import { ReactNode, cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { T, sans } from './portalTheme';
import { RatingHeaderChip, RatingSidebarCard, RatingStyles } from './portalRatingShared';
import portalApi from './portalApi';
import { usePortalAuth } from '../../context/PortalAuthContext';

/* The vendor portal's shared building blocks: the signed-out split-screen layout, the signed-in
   app shell (sidebar + header + user menu), and small primitives (buttons, fields, pills, progress,
   avatars, empty states) so every portal page reads as one product. The answering page
   (AnswerQuestionnaire.tsx) only uses FluxWatchMark from here and is intentionally not restyled. */

// ─── Brand ───────────────────────────────────────────────────────────────────────────────────

// The project's own logo (frontend/public/logo.png, the same file MainLayout.tsx uses for its
// light-theme header). `onDark` swaps to logo2.png, the white-text variant, for the sign-in panel.
export function FluxWatchMark({ small, onDark }: { small?: boolean; onDark?: boolean }) {
  return <img src={onDark ? '/logo2.png' : '/logo.png'} alt="FluxWatch" style={{ height: small ? 28 : 40, display: 'block' }} />;
}

// ─── Icons (inline SVG paths, stroke style) ──────────────────────────────────────────────────

export const IC = {
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  chevDown: 'M6 9l6 6 6-6',
  chevRight: 'M9 18l6-6-6-6',
  back: 'M19 12H5M12 19l-7-7 7-7',
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z',
  check: 'M20 6 9 17l-5-5',
  checkCircle: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  alert: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  mail: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2ZM22 6l-10 7L2 6',
  lock: 'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM7 11V7a5 5 0 0 1 10 0v4',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  eyeOff: 'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  plus: 'M12 5v14M5 12h14',
  close: 'M18 6 6 18M6 6l12 12',
  send: 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01',
  sparkle: 'M12 3l1.9 5.8L20 10.7l-5.8 1.9L12 18.5l-1.9-5.9L4 10.7l6.1-1.9L12 3Z',
};

export const Icon = ({ d, size = 16, sw = 2, color }: { d: string; size?: number; sw?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color || 'currentColor'} strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true"><path d={d} /></svg>
);

// ─── Primitives ──────────────────────────────────────────────────────────────────────────────

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export function Button({ children, variant = 'primary', size = 'md', loading, full, icon, style, ...rest }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'md' | 'lg'; loading?: boolean; full?: boolean; icon?: string }) {
  const pad = size === 'sm' ? '7px 12px' : size === 'lg' ? '12px 22px' : '9px 16px';
  const fs = size === 'sm' ? 12.5 : size === 'lg' ? 14.5 : 13.5;
  const palette: Record<BtnVariant, React.CSSProperties> = {
    primary: { background: T.primary, color: '#fff', border: `1px solid ${T.primary}` },
    secondary: { background: T.surface, color: T.ink, border: `1px solid ${T.bdr}` },
    ghost: { background: 'transparent', color: T.slate, border: '1px solid transparent' },
    danger: { background: T.surface, color: T.red, border: `1px solid ${T.redBdr}` },
  };
  const disabled = rest.disabled || loading;
  return (
    <button {...rest} disabled={disabled} className={`pw-btn pw-btn-${variant}`} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: pad, fontSize: fs, fontWeight: 600,
      fontFamily: sans, borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      width: full ? '100%' : undefined, whiteSpace: 'nowrap', lineHeight: 1.2, transition: 'background .12s, border-color .12s',
      ...palette[variant], ...style,
    }}>
      {loading ? <Spinner size={14} color={variant === 'primary' ? '#fff' : T.muted} /> : icon && <Icon d={icon} size={15} />}
      {children}
    </button>
  );
}

export function Spinner({ size = 18, color = T.primary }: { size?: number; color?: string }) {
  return (
    <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', border: `2px solid ${color}33`,
      borderTopColor: color, animation: 'pwSpin .7s linear infinite', flexShrink: 0 }} />
  );
}

export const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, fontFamily: sans, color: T.ink,
  background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 8, outline: 'none', textAlign: 'left',
};

export function Field({ label, hint, error, children, right }: { label: string; hint?: ReactNode; error?: string; children: ReactNode; right?: ReactNode }) {
  // Links the visible label to its control (htmlFor/id) so screen readers announce the field name.
  const id = useId();
  const control = isValidElement(children) ? cloneElement(children as React.ReactElement<any>, { id }) : children;
  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <label htmlFor={id} style={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>{label}</label>
        {right}
      </div>
      {control}
      {error ? <div style={{ fontSize: 12, color: T.red, marginTop: 5 }}>{error}</div>
        : hint ? <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>{hint}</div> : null}
    </div>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className="pw-input" style={{ ...inputStyle, ...(props.readOnly ? { background: T.surface2, color: T.slate } : {}), ...props.style }} />;
}

// Strength is a rough guide only (length + character variety); the backend's rule is 8+ chars.
export function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string; color: string } {
  if (!pw) return { score: 0, label: '', color: T.bdr };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  if (pw.length < 8) return { score: 1, label: 'Too short', color: T.red };
  const map = [
    { label: 'Weak', color: T.red }, { label: 'Weak', color: T.red }, { label: 'Fair', color: T.amber },
    { label: 'Good', color: T.green }, { label: 'Strong', color: T.green },
  ];
  return { score: s as 0 | 1 | 2 | 3 | 4, ...map[s] };
}

export function PasswordInput({ showStrength, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { showStrength?: boolean }) {
  const [visible, setVisible] = useState(false);
  const strength = passwordStrength(String(props.value || ''));
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <input {...props} type={visible ? 'text' : 'password'} className="pw-input" style={{ ...inputStyle, paddingRight: 40, ...props.style }} />
        <button type="button" onClick={() => setVisible(v => !v)} aria-label={visible ? 'Hide password' : 'Show password'}
          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none',
            cursor: 'pointer', color: T.faint, padding: 6, display: 'inline-flex' }}>
          <Icon d={visible ? IC.eyeOff : IC.eye} size={16} />
        </button>
      </div>
      {showStrength && props.value ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
          <div style={{ flex: 1, display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4].map(i => (
              <span key={i} style={{ flex: 1, height: 4, borderRadius: 99, background: i <= Math.max(strength.score, 1) ? strength.color : T.bdr }} />
            ))}
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: strength.color, minWidth: 54, textAlign: 'right' }}>{strength.label}</span>
        </div>
      ) : null}
    </div>
  );
}

export function Alert({ tone = 'info', children, icon }: { tone?: 'info' | 'error' | 'success' | 'warning'; children: ReactNode; icon?: string }) {
  const c = {
    info: { fg: T.primary, bg: T.blueBg, bdr: '#BFDBFE', ic: IC.help },
    error: { fg: T.red, bg: T.redBg, bdr: T.redBdr, ic: IC.alert },
    success: { fg: T.green, bg: T.greenBg, bdr: T.greenBdr, ic: IC.checkCircle },
    warning: { fg: T.amber, bg: T.amberBg, bdr: T.amberBdr, ic: IC.alert },
  }[tone];
  return (
    <div role={tone === 'error' ? 'alert' : undefined} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px',
      borderRadius: 9, background: c.bg, border: `1px solid ${c.bdr}`, color: c.fg, fontSize: 13, lineHeight: 1.5, textAlign: 'left' }}>
      <span style={{ marginTop: 1 }}><Icon d={icon || c.ic} size={16} /></span>
      <div style={{ color: tone === 'info' ? T.ink2 : c.fg }}>{children}</div>
    </div>
  );
}

export function Card({ children, style, padding = 24 }: { children: ReactNode; style?: React.CSSProperties; padding?: number | string }) {
  return <div style={{ background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 12, padding, ...style }}>{children}</div>;
}

export function CardHeader({ title, subtitle, action }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: T.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: T.muted, marginTop: 3, lineHeight: 1.5 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
  );
}

const AVATAR_COLORS = ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DB2777', '#0891B2', '#4F46E5', '#DC2626'];
export function Avatar({ name, size = 32, square }: { name: string; size?: number; square?: boolean }) {
  const clean = (name || '?').trim();
  const parts = clean.split(/[\s@._-]+/).filter(Boolean);
  const initials = ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[1][0] : '')).toUpperCase();
  let hash = 0;
  for (const ch of clean) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const color = AVATAR_COLORS[hash % AVATAR_COLORS.length];
  return (
    <span aria-hidden="true" style={{ width: size, height: size, borderRadius: square ? Math.round(size / 4) : '50%', flexShrink: 0,
      background: `${color}18`, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700, fontFamily: sans }}>
      {initials}
    </span>
  );
}

export function Pill({ color, bg, children, dot = true }: { color: string; bg: string; children: ReactNode; dot?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color, background: bg,
      padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap', lineHeight: 1.5 }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />}
      {children}
    </span>
  );
}

export type ResponseStatus = 'sent' | 'opened' | 'answered' | 'reviewed';
export const STATUS_META: Record<ResponseStatus, { label: string; color: string; bg: string }> = {
  sent: { label: 'Not started', color: T.slate, bg: T.hover },
  opened: { label: 'In progress', color: T.primary, bg: T.blueBg },
  answered: { label: 'Submitted', color: T.violet, bg: T.violetBg },
  reviewed: { label: 'Completed', color: T.green, bg: T.greenBg },
};
export function StatusPill({ status }: { status: ResponseStatus }) {
  const m = STATUS_META[status] || STATUS_META.sent;
  return <Pill color={m.color} bg={m.bg}>{m.label}</Pill>;
}

export const daysUntil = (iso: string) => {
  const d = new Date(iso.slice(0, 10) + 'T00:00:00');
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - now.getTime()) / 86400000);
};
export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

// Due date shown with urgency -- only for work still open (sent/opened); a submitted questionnaire
// isn't "overdue" any more.
export function DueBadge({ due, status }: { due: string | null; status: ResponseStatus }) {
  if (!due) return <span style={{ fontSize: 13, color: T.faint }}>No due date</span>;
  const open = status === 'sent' || status === 'opened';
  const d = daysUntil(due);
  const tone = !open ? null : d < 0 ? 'overdue' : d <= 7 ? 'soon' : null;
  return (
    <div>
      <div style={{ fontSize: 13, color: T.ink2, fontWeight: 500 }}>{fmtDate(due)}</div>
      {tone === 'overdue' && <div style={{ fontSize: 11.5, fontWeight: 700, color: T.red, marginTop: 2 }}>Overdue by {-d} day{d === -1 ? '' : 's'}</div>}
      {tone === 'soon' && <div style={{ fontSize: 11.5, fontWeight: 700, color: T.amber, marginTop: 2 }}>{d === 0 ? 'Due today' : `Due in ${d} day${d === 1 ? '' : 's'}`}</div>}
    </div>
  );
}

export function ProgressBar({ value, total, height = 6, showLabel }: { value: number; total: number; height?: number; showLabel?: boolean }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const color = pct === 100 ? T.green : T.primary;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <div style={{ flex: 1, height, borderRadius: 99, background: T.hover, overflow: 'hidden', minWidth: 40 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width .25s' }} />
      </div>
      {showLabel && <span style={{ fontSize: 12, fontWeight: 600, color: T.slate, fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>{pct}%</span>}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon: string; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px' }}>
      <span style={{ width: 48, height: 48, borderRadius: 14, background: T.blueBg, color: T.primary, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Icon d={icon} size={22} />
      </span>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.ink, marginBottom: 6 }}>{title}</div>
      {children && <div style={{ fontSize: 13.5, color: T.muted, maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>{children}</div>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </div>
  );
}

// One global stylesheet for hover/focus states and animations inline styles can't express.
export function PortalGlobalStyles() {
  return (
    <style>{`
      @keyframes pwSpin { to { transform: rotate(360deg); } }
      @keyframes pwFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      .pw-input:focus { border-color: ${T.primary} !important; box-shadow: 0 0 0 3px ${T.blueBg}; }
      .pw-btn-primary:not(:disabled):hover { background: ${T.primaryHover} !important; border-color: ${T.primaryHover} !important; }
      .pw-btn-secondary:not(:disabled):hover { background: ${T.hover} !important; }
      .pw-btn-ghost:not(:disabled):hover { background: ${T.hover} !important; color: ${T.ink} !important; }
      .pw-btn-danger:not(:disabled):hover { background: ${T.redBg} !important; }
      .pw-row:hover { background: ${T.surface2}; }
      .pw-nav:hover { background: ${T.hover}; color: ${T.ink}; }
      .pw-link:hover { text-decoration: underline; }
      @media (max-width: 1180px) { .pw-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
      @media (max-width: 520px) { .pw-tiles { grid-template-columns: 1fr !important; } }
      @media (max-width: 960px) { .pw-auth-panel { display: none !important; } .pw-sidebar { display: none !important; } .pw-mobile-nav { display: flex !important; } }
      @media (max-width: 720px) { .pw-hide-sm { display: none !important; } .pw-grid-2 { grid-template-columns: 1fr !important; } }
    `}</style>
  );
}

// ─── Signed-out layout (sign in / sign up / reset / invite landing) ─────────────────────────

export function AuthLayout({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  const features = [
    { icon: IC.inbox, title: 'Every request in one inbox', body: 'Security questionnaires from every company you work with, with due dates and progress at a glance.' },
    { icon: IC.file, title: 'Upload evidence once', body: 'Keep SOC 2 reports, policies and certificates in one library and attach them to any answer.' },
    { icon: IC.users, title: 'Answer as a team', body: 'Invite colleagues to help with the questions they know best. You stay in control of submitting.' },
  ];
  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: sans, background: T.surface }}>
      <PortalGlobalStyles />
      <div className="pw-auth-panel" style={{ width: '44%', maxWidth: 560, background: T.panel, color: '#fff', padding: '44px 52px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 40, boxSizing: 'border-box' }}>
        <div style={{ flexShrink: 0 }}>
          <FluxWatchMark onDark />
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#93C5FD', marginTop: 48 }}>Vendor portal</div>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.25, marginTop: 12, letterSpacing: -0.4 }}>
            Respond to security reviews in one place.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginTop: 40 }}>
            {features.map(f => (
              <div key={f.title} style={{ display: 'flex', gap: 14 }}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,.08)', color: '#93C5FD',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon d={f.icon} size={18} />
                </span>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700 }}>{f.title}</div>
                  <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.55, marginTop: 3 }}>{f.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12, color: '#94A3B8', lineHeight: 1.6, flexShrink: 0 }}>
          <Icon d={IC.shield} size={15} />
          <span>Your answers and documents are shared only with the company that sent you the request.</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div style={{ width: '100%', maxWidth: 420, animation: 'pwFadeIn .25s ease-out' }}>{children}</div>
        </div>
        <div style={{ padding: '18px 24px', textAlign: 'center', fontSize: 12, color: T.faint }}>
          {footer || <>© {new Date().getFullYear()} FluxVell Technologies · FluxWatch vendor portal</>}
        </div>
      </div>
    </div>
  );
}

// Heading block used at the top of every signed-out form.
export function AuthHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div className="pw-mobile-nav" style={{ display: 'none', marginBottom: 24 }}><FluxWatchMark small /></div>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: -0.3 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 14, color: T.muted, marginTop: 8, lineHeight: 1.55 }}>{subtitle}</div>}
    </div>
  );
}

// Kept for any older caller; new signed-out pages use AuthLayout.
export function PortalShell({ children }: { children: ReactNode }) {
  return <AuthLayout>{children}</AuthLayout>;
}
export function PortalCard({ children }: { children: ReactNode; maxWidth?: number }) {
  return <>{children}</>;
}
export const primaryButton = (disabled?: boolean): React.CSSProperties => ({
  width: '100%', boxSizing: 'border-box', padding: '11px 24px', borderRadius: 8, border: 'none',
  background: T.primary, color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: sans,
  cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.7 : 1,
});

// ─── Auth guard ──────────────────────────────────────────────────────────────────────────────

// Mirrors App.tsx's tenant ProtectedRoute, but reads PortalAuthContext instead -- an
// unauthenticated visit to the dashboard or the answer page bounces to the portal login, not the
// tenant app's /login.
export function PortalProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = usePortalAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to={`/questionnaire/portal/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

// ─── Signed-in shell ─────────────────────────────────────────────────────────────────────────

export interface PortalResponseRow {
  id: string;
  status: ResponseStatus;
  due_date: string | null;
  sent_at: string;
  answered_count: number;
  questionnaire_name: string;
  total_items: number;
  vendor_name: string;
  sender_org_name: string;
  message?: string | null;
  is_collaborator: boolean;
}

// The dashboard and the shell's "open" badge share this one query, so they share one cache entry.
export function usePortalInbox() {
  return useQuery({
    queryKey: ['portal', 'me'],
    queryFn: async () => (await portalApi.get('/v1/questionnaire-responses/portal/me')).data as {
      account: { id: string; email: string; name: string | null }; responses: PortalResponseRow[];
    },
  });
}

export interface PortalRemediationRow {
  id: string;
  status: 'in_progress' | 'awaiting_review' | 'completed';
  updated_at: string | null;
  // This recipient's own last_seen_at for the request (set server-side every time the detail page is
  // opened) -- null means never opened. Compare against updated_at with isRemediationUnseen() below.
  recipient_last_seen_at: string | null;
}

// A request counts as "unseen" the first time it's ever sent, and again any time something changes
// about it (a new message, a status flip, a scan confirming or reopening a risk -- anything that bumps
// the request's own updated_at) after the vendor last opened its detail page. A completed request still
// shows as unseen once if the completion itself hasn't been seen yet.
export function isRemediationUnseen(r: PortalRemediationRow): boolean {
  if (!r.updated_at) return false;
  if (!r.recipient_last_seen_at) return true;
  return new Date(r.updated_at).getTime() > new Date(r.recipient_last_seen_at).getTime();
}

// The sidebar badge and the Remediation list page share this one query/cache entry.
export function usePortalRemediationList<T extends PortalRemediationRow = PortalRemediationRow>() {
  return useQuery({
    queryKey: ['portal', 'remediation'],
    queryFn: async () => (await portalApi.get('/v1/questionnaire-responses/portal/remediation')).data.requests as T[],
  });
}

function UserMenu() {
  const { account, logout } = usePortalAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const display = account?.name || account?.email || '';
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} className="pw-nav"
        style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: '5px 8px 5px 5px',
          borderRadius: 9, fontFamily: sans, color: T.ink }}>
        <Avatar name={display} size={30} />
        <span className="pw-hide-sm" style={{ fontSize: 13, fontWeight: 600, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
        <Icon d={IC.chevDown} size={14} color={T.muted} />
      </button>
      {open && (
        <div role="menu" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 250, background: T.surface, border: `1px solid ${T.bdr}`,
          borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,42,.12)', padding: 6, zIndex: 50, animation: 'pwFadeIn .12s ease-out' }}>
          <div style={{ padding: '10px 10px 12px', borderBottom: `1px solid ${T.bdr}`, marginBottom: 6 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>{account?.name || 'Your account'}</div>
            <div style={{ fontSize: 12, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{account?.email}</div>
          </div>
          {[
            { label: 'Account settings', icon: IC.user, onClick: () => navigate('/questionnaire/portal/account') },
            { label: 'Sign out', icon: IC.logout, onClick: () => { logout(); navigate('/questionnaire/portal/login', { replace: true }); } },
          ].map(item => (
            <button key={item.label} role="menuitem" onClick={() => { setOpen(false); item.onClick(); }} className="pw-nav"
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 10px', background: 'none', border: 'none',
                borderRadius: 7, cursor: 'pointer', fontSize: 13, color: T.ink2, fontFamily: sans, textAlign: 'left' }}>
              <Icon d={item.icon} size={15} color={T.muted} /> {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// A small red count badge shown to the right of a nav item's label (not pinned to the icon) --
// used by both the sidebar and the mobile nav row.
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span aria-hidden style={{ minWidth: 16, height: 16, borderRadius: 99, background: T.red, color: '#fff',
      fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center', padding: '0 4px', flexShrink: 0 }}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

const NAV = [
  { to: '/questionnaire/portal/dashboard', label: 'Questionnaires', icon: IC.inbox, match: ['/questionnaire/portal/dashboard', '/questionnaire/portal/response'] },
  { to: '/questionnaire/portal/documents', label: 'Documents', icon: IC.file, match: ['/questionnaire/portal/documents'] },
  { to: '/questionnaire/portal/remediation', label: 'Remediation', icon: IC.shield, match: ['/questionnaire/portal/remediation'] },
  { to: '/questionnaire/portal/rating', label: 'Security rating', icon: IC.activity, match: ['/questionnaire/portal/rating'] },
  { to: '/questionnaire/portal/account', label: 'Account', icon: IC.user, match: ['/questionnaire/portal/account'] },
];

// The signed-in shell every authenticated portal page (dashboard, questionnaire overview,
// documents, account) sits inside: a header with the logo and user menu, a left nav, and the page.
export function PortalAppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const inbox = usePortalInbox();
  const openCount = (inbox.data?.responses || []).filter(r => r.status === 'sent' || r.status === 'opened').length;
  const remediation = usePortalRemediationList();
  const remediationUnseenCount = (remediation.data || []).filter(isRemediationUnseen).length;
  const isActive = (match: string[]) => match.some(m => location.pathname.startsWith(m));

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: sans, display: 'flex', flexDirection: 'column' }}>
      <PortalGlobalStyles />
      <RatingStyles />
      <header style={{ height: 64, background: T.surface, borderBottom: `1px solid ${T.bdr}`, padding: '0 24px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 30, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/questionnaire/portal/dashboard" aria-label="Home"><FluxWatchMark small /></Link>
          <span className="pw-hide-sm" style={{ fontSize: 12, fontWeight: 700, color: T.slate, background: T.hover, padding: '4px 10px', borderRadius: 99 }}>Vendor portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <RatingHeaderChip />
          <UserMenu />
        </div>
      </header>

      <nav className="pw-mobile-nav" style={{ display: 'none', gap: 4, padding: '8px 16px', background: T.surface, borderBottom: `1px solid ${T.bdr}`, overflowX: 'auto' }}>
        {NAV.map(n => (
          <Link key={n.to} to={n.to} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 12px', borderRadius: 7, fontSize: 13, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
            color: isActive(n.match) ? T.primary : T.slate, background: isActive(n.match) ? T.blueBg : 'transparent' }}>
            {n.label}
            {n.label === 'Remediation' && <NavBadge count={remediationUnseenCount} />}
            {n.label === 'Questionnaires' && <NavBadge count={openCount} />}
          </Link>
        ))}
      </nav>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <aside className="pw-sidebar" style={{ width: 236, flexShrink: 0, background: T.surface, borderRight: `1px solid ${T.bdr}`, padding: '20px 14px',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'sticky', top: 64, height: 'calc(100vh - 64px)', boxSizing: 'border-box' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: T.faint, textTransform: 'uppercase', padding: '0 10px', marginBottom: 8 }}>Menu</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {NAV.map(n => {
                const active = isActive(n.match);
                return (
                  <Link key={n.to} to={n.to} className={active ? undefined : 'pw-nav'} aria-current={active ? 'page' : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, textDecoration: 'none',
                      fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? T.primary : T.slate, background: active ? T.blueBg : 'transparent' }}>
                    <Icon d={n.icon} size={17} />
                    <span style={{ flex: 1 }}>{n.label}</span>
                    {n.label === 'Remediation' && <NavBadge count={remediationUnseenCount} />}
                    {n.label === 'Questionnaires' && <NavBadge count={openCount} />}
                  </Link>
                );
              })}
            </div>
          </div>
          <div>
          <RatingSidebarCard />
          <div style={{ background: T.surface2, border: `1px solid ${T.bdr}`, borderRadius: 10, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: T.ink2 }}>
              <Icon d={IC.help} size={15} color={T.primary} /> Need help?
            </div>
            <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.55, marginTop: 6 }}>
              Questions about a request? Reply to the email from the company that sent it — they own the questionnaire.
            </div>
          </div>
          </div>
        </aside>

        <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
      </div>
    </div>
  );
}

// Page container + standard heading for signed-in pages.
export function PageHeader({ title, subtitle, action, breadcrumb }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; breadcrumb?: ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {breadcrumb && <div style={{ marginBottom: 12 }}>{breadcrumb}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: T.ink, margin: 0, letterSpacing: -0.4 }}>{title}</h1>
          {subtitle && <div style={{ fontSize: 14, color: T.muted, marginTop: 6, lineHeight: 1.55 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
    </div>
  );
}

// No `max` (the default): fills the whole main content area, just with side padding -- the same
// fluid convention the main FluxWatch app's own pages use (e.g. Vendors.tsx has no width cap at
// all). Pass `max` only for a page that genuinely wants a narrower reading width, like the Account
// settings form -- a left-over fixed 1120px cap here used to leave large empty margins on anything
// wider than that, which is the bug this default fixes.
export function PageContainer({ children, max }: { children: ReactNode; max?: number }) {
  return <div style={{ maxWidth: max, margin: max ? '0 auto' : undefined, padding: '32px 28px 64px', animation: 'pwFadeIn .2s ease-out' }}>{children}</div>;
}

const STEPS = ['Sent', 'Opened', 'Submitted', 'Completed'] as const;

// Vertical Sent -> Opened -> Submitted -> Completed timeline.
// `dates` gives each reached step's timestamp (undefined for one not reached yet).
export function VerticalStepper({ stage, dates }: { stage: number; dates: (string | null)[] }) {
  return (
    <div>
      {STEPS.map((label, i) => {
        const reached = i <= stage;
        const isLast = i === STEPS.length - 1;
        return (
          <div key={label} style={{ display: 'flex', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: reached ? T.primary : T.surface, border: `2px solid ${reached ? T.primary : T.bdr}`, color: '#fff' }}>
                {reached && <Icon d={IC.check} size={12} sw={3} />}
              </span>
              {!isLast && <div style={{ width: 2, flex: 1, minHeight: 26, background: i < stage ? T.primary : T.bdr }} />}
            </div>
            <div style={{ paddingBottom: isLast ? 0 : 20 }}>
              <div style={{ fontSize: 14, fontWeight: reached ? 700 : 500, color: reached ? T.ink : T.faint, lineHeight: '22px' }}>{label}</div>
              {dates[i] && <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>{fmtDate(dates[i])}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
